import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from '../utils/remarkGfmSafe';
import { getBlogPost } from '../services/api';
import { ArrowLeft, Calendar, RefreshCcw, ListOrdered, User } from 'lucide-react';
import SEO from './SEO';
import { imgErrorFallback } from '../utils/imageFallback';
import ShareButtons from './ShareButtons';
import RelatedContent from './RelatedContent';
import BlogComments from './BlogComments';

const SITE_URL = 'https://camption.com';

const slugify = (s = '') =>
  s
    .toLowerCase()
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ş/g, 's')
    .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// Detects if the content is markdown (starts with # or has clear markdown syntax) vs HTML.
const looksLikeMarkdown = (content = '') => {
  const trimmed = content.trim();
  if (trimmed.startsWith('<')) return false;
  return /(^|\n)#{1,6}\s|\n\|.+\|/.test(trimmed);
};

// Extracts H2/H3 headings from markdown for table of contents.
const extractHeadings = (md = '') => {
  const lines = md.split('\n');
  const items = [];
  for (const line of lines) {
    const m = line.match(/^(#{2,3})\s+(.+)/);
    if (m) items.push({ level: m[1].length, text: m[2].trim(), id: slugify(m[2]) });
  }
  return items;
};

const markdownComponents = {
  h2: ({ node, children, ...props }) => {
    const text = String(children).replace(/,/g, '');
    return (
      <h2 id={slugify(text)} className="text-2xl md:text-3xl font-bold text-[#1B1B1B] mt-12 mb-4 scroll-mt-24" {...props}>
        {children}
      </h2>
    );
  },
  h3: ({ node, children, ...props }) => {
    const text = String(children).replace(/,/g, '');
    return (
      <h3 id={slugify(text)} className="text-xl md:text-2xl font-semibold text-[#1B1B1B] mt-8 mb-3 scroll-mt-24" {...props}>
        {children}
      </h3>
    );
  },
  table: ({ children, ...props }) => (
    <div className="my-6 -mx-4 md:mx-0 overflow-x-auto">
      <table className="min-w-full border-collapse text-sm border border-gray-200" {...props}>
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...props }) => (
    <th className="border border-gray-200 bg-[#F5F5F0] text-left px-3 py-2 font-semibold text-[#1B1B1B]" {...props}>
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td className="border border-gray-200 px-3 py-2 text-gray-700" {...props}>
      {children}
    </td>
  ),
  a: ({ children, href, ...props }) => (
    <a
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="text-[#3D4A26] underline hover:text-[#C8D843]"
      {...props}
    >
      {children}
    </a>
  ),
  img: ({ src, alt, ...props }) => (
    <img src={src} alt={alt || ''} loading="lazy" className="rounded-lg my-4 w-full" {...props} />
  ),
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getBlogPost(slug)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  const isMarkdown = useMemo(() => (post ? looksLikeMarkdown(post.content || '') : false), [post]);
  const toc = useMemo(() => (isMarkdown && post ? extractHeadings(post.content || '') : []), [post, isMarkdown]);

  if (loading) {
    return <div className="py-20 text-center text-gray-500">Yükleniyor...</div>;
  }

  if (notFound || !post) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-[#1B1B1B] mb-3">Yazı bulunamadı</h1>
        <p className="text-gray-500 mb-6">Aradığınız blog yazısı yayında değil olabilir.</p>
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#3D4A26] hover:underline">
          <ArrowLeft size={14} /> Blog listesine dön
        </Link>
      </div>
    );
  }

  const publishedAt = post.publishedAt || post.created_at;
  const updatedAt = post.updatedAt || post.updated_at || publishedAt;

  const fmt = (d) =>
    d ? new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const canonical = `${SITE_URL}/blog/${post.slug}`;
  const metaTitle = post.metaTitle || post.title;
  const metaDescription = post.metaDescription || post.excerpt || post.title;
  const ogImage = post.image || undefined;

  // Author defaults for Camption
  const author = post.author || {
    name: 'Baturalp Aktepe',
    jobTitle: 'Kokartlı Turist Rehberi',
    bio:
      "Turist Rehberliği mezunu, 2019'dan bu yana Turizm Bakanlığı'na bağlı ülkesel turist rehberi. On yıla yakın Likya Yolu deneyimi.",
    image: post.authorImage || '',
  };

  // Build @graph JSON-LD: Article + BreadcrumbList + FAQPage (if faqItems)
  const graph = [
    {
      '@type': 'Article',
      '@id': `${canonical}#article`,
      headline: post.title,
      description: metaDescription,
      inLanguage: 'tr-TR',
      datePublished: publishedAt ? new Date(publishedAt).toISOString() : undefined,
      dateModified: updatedAt ? new Date(updatedAt).toISOString() : undefined,
      image: ogImage ? [ogImage] : undefined,
      author: {
        '@type': 'Person',
        name: author.name,
        jobTitle: author.jobTitle,
        description: author.bio,
        worksFor: { '@id': `${SITE_URL}/#organization` },
      },
      publisher: { '@id': `${SITE_URL}/#organization` },
      mainEntityOfPage: { '@id': canonical },
    },
    {
      '@type': 'TravelAgency',
      '@id': `${SITE_URL}/#organization`,
      name: 'Camption',
      url: SITE_URL,
      email: 'hello@camption.com',
      telephone: '+908504959524',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Andifli, Nevzat Hacıoğlu Sokak No:3',
        addressLocality: 'Kaş',
        addressRegion: 'Antalya',
        postalCode: '07580',
        addressCountry: 'TR',
      },
      areaServed: 'Likya Yolu, Karya Yolu',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title },
      ],
    },
  ];

  const faqItems = Array.isArray(post.faqItems) ? post.faqItems.filter((f) => f.question && f.answer) : [];
  if (faqItems.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: faqItems.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  const jsonLd = { '@context': 'https://schema.org', '@graph': graph };

  return (
    <article className="bg-white" data-testid="blog-detail">
      <SEO
        title={metaTitle}
        description={metaDescription}
        image={ogImage}
        url={canonical}
        type="article"
        jsonLd={jsonLd}
      />

      {post.image && (
        <div className="w-full h-[300px] md:h-[420px] overflow-hidden bg-gray-100">
          <img
            src={post.image}
            alt={post.imageAlt || post.title}
            onError={imgErrorFallback}
            loading="eager"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {/* Breadcrumbs (visible) */}
        <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-4" data-testid="blog-breadcrumb">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-[#3D4A26]">Ana Sayfa</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to="/blog" className="hover:text-[#3D4A26]">Blog</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-700 line-clamp-1">{post.title}</li>
          </ol>
        </nav>

        {post.category && (
          <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-3 block">
            {post.category}
          </span>
        )}
        <h1
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B1B] leading-tight mb-4"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-8" data-testid="blog-meta">
          {publishedAt && (
            <span className="flex items-center gap-1.5">
              <Calendar size={14} /> {fmt(publishedAt)}
            </span>
          )}
          {updatedAt && updatedAt !== publishedAt && (
            <span className="flex items-center gap-1.5">
              <RefreshCcw size={14} /> Güncelleme: {fmt(updatedAt)}
            </span>
          )}
          {author?.name && (
            <span className="flex items-center gap-1.5">
              <User size={14} /> {author.name}
            </span>
          )}
        </div>

        {post.excerpt && (
          <p className="text-lg text-gray-700 leading-relaxed mb-8 border-l-4 border-[#C8D843] pl-4 italic">
            {post.excerpt}
          </p>
        )}

        {/* Table of Contents */}
        {toc.length >= 3 && (
          <aside
            data-testid="blog-toc"
            className="mb-10 rounded-xl border border-gray-200 bg-[#FAFAF7] p-5"
          >
            <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#3D4A26] font-semibold mb-3">
              <ListOrdered size={14} /> İçindekiler
            </p>
            <ol className="space-y-1.5 text-sm">
              {toc.map((h, i) => (
                <li key={i} className={h.level === 3 ? 'pl-4' : ''}>
                  <a href={`#${h.id}`} className="text-[#1B1B1B] hover:text-[#3D4A26] hover:underline">
                    {h.text}
                  </a>
                </li>
              ))}
            </ol>
          </aside>
        )}

        {isMarkdown ? (
          <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-[#1B1B1B] prose-a:text-[#3D4A26]">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {post.content || ''}
            </ReactMarkdown>
          </div>
        ) : (
          <div
            className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-[#1B1B1B] prose-a:text-[#3D4A26]"
            dangerouslySetInnerHTML={{ __html: post.content || '' }}
          />
        )}

        {/* Author box (E-E-A-T) */}
        {author?.name && (
          <div
            data-testid="blog-author-box"
            className="mt-14 border-t border-gray-200 pt-8 flex items-start gap-4"
          >
            {author.image ? (
              <img src={author.image} alt={author.name} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#3D4A26] text-white flex items-center justify-center font-bold text-lg">
                {author.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
              </div>
            )}
            <div>
              <p className="font-bold text-[#1B1B1B]">{author.name}</p>
              {author.jobTitle && <p className="text-xs text-[#3D4A26] font-semibold mb-1">{author.jobTitle}</p>}
              {author.bio && <p className="text-sm text-gray-600 leading-relaxed">{author.bio}</p>}
            </div>
          </div>
        )}

        {/* Share buttons */}
        <ShareButtons title={post.title} url={canonical} />

        {/* Related content: relevant blog posts + tours */}
        <RelatedContent
          excludeSlug={post.slug}
          matchCategory={post.category}
          matchKeyphrase={post.focusKeyphrase || post.title}
          heading="Devamını Okuyun"
        />

        {/* Comments (moderated) */}
        <BlogComments postSlug={post.slug} />
      </div>
    </article>
  );
};

export default BlogDetail;
